import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { BridgeStatusChip } from '@/components/ui/status-chip';
import { listOrganizations } from '@/services/admin/organization-service';
import type { Organization } from '@/services/admin/fhir/types';

/**
 * SuperAdmin → Organisations list. Reads `GET /api/v1/Organization/list` via
 * the FHIR adapter; the component only ever touches FHIR `Organization[]`.
 *
 * Columns:
 *   Name           — Organization.name
 *   License type   — Organization.identifier[system=license].type.text
 *   License #      — Organization.identifier[system=license].value
 *   Status         — StatusBadge from Organization.active
 *   Sub-orgs       — extension[url=subOrganizationCount].valueInteger
 */
export function OrgListPage() {
  const [search, setSearch] = useState('');
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'organizations', search],
    queryFn: ({ signal }) =>
      listOrganizations({ q: search || undefined }, { signal }),
    staleTime: 30_000,
  });

  const columns = useMemo<ColumnDef<Organization>[]>(
    () => [
      {
        header: 'Name',
        accessorFn: (row) => row.name ?? '—',
        id: 'name',
        cell: (info) => (
          <span className="[font:var(--type-body-strong)] text-[var(--ink-body)]">
            {String(info.getValue() ?? '—')}
          </span>
        ),
      },
      {
        header: 'License type',
        accessorFn: (row) => licenseIdentifier(row)?.type?.text ?? '—',
        id: 'licenseType',
      },
      {
        header: 'License #',
        accessorFn: (row) => licenseIdentifier(row)?.value ?? '—',
        id: 'licenseNumber',
      },
      {
        header: 'Status',
        id: 'status',
        cell: (info) => {
          const active = (info.row.original.active ?? true) as boolean;
          return (
            <BridgeStatusChip semantic={active ? 'success' : 'neutral'}>
              {active ? 'Active' : 'Inactive'}
            </BridgeStatusChip>
          );
        },
      },
      {
        header: 'Sub-orgs',
        accessorFn: (row) => subOrgCount(row),
        id: 'subOrgs',
      },
    ],
    [],
  );

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="[font:var(--type-page-title)] text-[var(--ink-body)]">Organisations</h1>
          <p className="[font:var(--type-body)] text-[var(--ink-secondary)]">Tenants registered in Scrips</p>
        </div>
        <Input
          placeholder="Search by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          aria-label="Search organisations"
        />
      </div>

      {isError && (
        <div
          role="alert"
          className="[font:var(--type-body)] text-[var(--status-bad)] bg-[var(--status-overdue-bg)] rounded-md px-3 py-2 flex items-center gap-3"
        >
          <span>
            Couldn't load organisations — {error instanceof Error ? error.message : 'unknown error'}.
          </span>
          <button
            type="button"
            onClick={() => refetch()}
            className="[font:var(--type-label)] text-[var(--status-bad)] underline"
          >
            Retry
          </button>
        </div>
      )}

      <div className="list-table">
        <table className="w-full text-left">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`skel-${i}`}>
                  {columns.map((_c, j) => (
                    <td key={j} className="px-4 py-3">
                      <Skeleton className="h-4 w-32" />
                    </td>
                  ))}
                </tr>
              ))}

            {!isLoading &&
              !isError &&
              data &&
              data.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-12 text-center [font:var(--type-body)] text-[var(--ink-meta)]"
                  >
                    No organisations match this search.
                  </td>
                </tr>
              )}

            {!isLoading &&
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="text-text-primary">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell ?? ((v) => v.getValue()), cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function licenseIdentifier(org: Organization) {
  return org.identifier?.find((i) => i.system === 'https://scrips.com/license');
}

function subOrgCount(org: Organization): number {
  const ext = org.extension?.find(
    (e) => e.url === 'https://scrips.com/fhir/ext/subOrganizationCount',
  );
  return typeof ext?.valueInteger === 'number' ? ext.valueInteger : 0;
}
